import {DateTimeFormat} from '@hconnect/common/components/dateTime/DateTimeFormat'
import {DateTime, WorkOrderTarget, ShiftEvent} from '@hconnect/common/types'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {Typography} from '@mui/material'
import moment, {Moment} from 'moment-timezone'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {hasMaintenanceNotificationCreationTimedOut} from '../../common/utils/eventDetailsHelper'
import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../../consts'
import {useSapWorkOrders} from '../../hooks/api/useSapWorkOrders'

type InfoProps = {
  plantId: string
  label?: string
  date?: DateTime | undefined
  userName?: string
  'data-test-id'?: string
  component?: React.ElementType
}

type EventInfoProps = {
  item: Partial<ShiftEvent>
  sapNotificationFailed?: boolean
  workOrderTarget?: WorkOrderTarget
  plantId: string
}

export const Info: React.FC<InfoProps> = ({
  plantId,
  label,
  userName,
  date,
  'data-test-id': dataTestId,
  component
}) => {
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const timezone = useUpmPlantTimezone(plantId)

  return (
    <Typography
      component={component ?? 'p'}
      gutterBottom
      variant="caption"
      data-test-id={dataTestId}
    >
      {label && `${label} `}
      {userName && date && (
        <>
          {userName} {t('eventsTable.happenOn')} <DateTimeFormat date={date} timezone={timezone} />
        </>
      )}
    </Typography>
  )
}

export const WorkOrders: React.FC<{plantId: string; notificationId: string}> = ({
  plantId,
  notificationId
}) => {
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const {data, error} = useSapWorkOrders({plantId, notificationId})

  return (
    <>
      {error &&
        (error?.response?.status === 404 ? (
          <Typography
            component={'p'}
            gutterBottom
            variant={'caption'}
            data-test-id="work-orders-no-wo-created"
          >
            {t('shiftEvent.label.noWorkOrderCreated')}
          </Typography>
        ) : (
          <Typography
            component={'p'}
            gutterBottom
            variant={'caption'}
            color={'error'}
            data-test-id="work-orders-error"
          >
            {t('shiftEvent.label.workOrderError', {errorMessage: error.message})}
          </Typography>
        ))}
      {data && (
        <Typography
          component={'p'}
          gutterBottom
          variant={'caption'}
          key={data.id}
          data-test-id={`work-order-${data.id}`}
        >
          {t('shiftEvent.label.workOrder', {workOrderId: data.id})}
        </Typography>
      )}
    </>
  )
}

const SapMaintenanceNotificationDetails = ({
  item,
  notificationFailed,
  plantId
}: {
  item: Partial<ShiftEvent>
  notificationFailed: boolean | undefined
  plantId: string
}) => {
  const timezone = useUpmPlantTimezone(plantId)
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const sapMaintenanceNotificationId = item.sapMaintenanceNotificationId
  const hasNotificationCreationInfo =
    item.sapMaintenanceNotificationCreator?.name && item.lastMaintenanceNotificationCreationStart

  return (
    <>
      {(hasMaintenanceNotificationCreationTimedOut(item, moment.tz(timezone)) ||
        notificationFailed) && (
        <>
          <Typography
            component="p"
            pt={0.5}
            variant="caption"
            color="error"
            data-test-id="event-info-sap-notification-error"
          >
            {t('shiftEvent.error.maintenanceNotificationCreationTimeout')}{' '}
          </Typography>
          {hasNotificationCreationInfo && (
            <Info
              plantId={plantId}
              component="span"
              label={t('shiftEvent.label.sentBy')}
              userName={item.sapMaintenanceNotificationCreator?.name}
              date={item.lastMaintenanceNotificationCreationStart}
              data-test-id="event-info-sap-notification-error-creator-info"
            />
          )}
        </>
      )}
      {sapMaintenanceNotificationId && (
        <Info
          plantId={plantId}
          label={`${t('shiftEvent.label.sapPmNotificationId')}: ${sapMaintenanceNotificationId} ${
            hasNotificationCreationInfo ? t('shiftEvent.label.sentBy') : ''
          }`}
          data-test-id="event-info-sap-notification-label"
          userName={item.sapMaintenanceNotificationCreator?.name}
          date={item.lastMaintenanceNotificationCreationStart}
        />
      )}
      {sapMaintenanceNotificationId && (
        <WorkOrders plantId={plantId} notificationId={sapMaintenanceNotificationId} />
      )}
    </>
  )
}

const InforMaintenanceNotificationDetails = ({
  item,
  notificationFailed
}: {
  item: Partial<ShiftEvent>
  notificationFailed: boolean | undefined
}) => {
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const inforWorkOrderRequestId = item.inforWorkOrder?.requestId

  return (
    <>
      {notificationFailed && (
        <Typography
          variant="caption"
          color="error"
          data-test-id="event-info-infor-notification-error"
        >
          {t('shiftEvent.error.maintenanceNotificationCreationTimeout')}
        </Typography>
      )}
      {!!inforWorkOrderRequestId && (
        <Typography variant="caption" data-test-id="event-info-infor-notification-label">
          {t('shiftEvent.label.inforWorkOrderRequestId')}: {inforWorkOrderRequestId}
        </Typography>
      )}
    </>
  )
}

type EventCreatorProps = {
  plantId: string
  name?: string
  created?: Moment
  lastModified?: Moment
  modifiedBy?: string
}
export const EventCreator: React.FC<EventCreatorProps> = ({
  plantId,
  name,
  created,
  lastModified,
  modifiedBy
}) => {
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  return (
    <>
      <Info
        plantId={plantId}
        label={t('shiftEvent.label.createdBy')}
        userName={name ?? ''}
        date={created}
        data-test-id="meta-annotation-created-by"
      />
      {lastModified && !lastModified.isSame(created) && (
        <Info
          plantId={plantId}
          label={t('shiftEvent.label.modifiedBy')}
          userName={modifiedBy ?? ''}
          date={lastModified}
          data-test-id="meta-annotation-modified-by"
        />
      )}
    </>
  )
}

export const EventInfo: React.FC<EventInfoProps> = ({
  item,
  sapNotificationFailed,
  workOrderTarget,
  plantId
}) => {
  const isInforWorkOrder = workOrderTarget === WorkOrderTarget.Infor
  const isSapWorkOrder = workOrderTarget === WorkOrderTarget.Sap

  return (
    <>
      <EventCreator
        plantId={plantId}
        name={item.createdBy?.name}
        created={item.created}
        modifiedBy={item.modifiedBy}
        lastModified={item.lastModified}
      />
      {isSapWorkOrder && (
        <SapMaintenanceNotificationDetails
          plantId={plantId}
          item={item}
          notificationFailed={sapNotificationFailed}
        />
      )}
      {isInforWorkOrder && (
        <InforMaintenanceNotificationDetails
          item={item}
          notificationFailed={sapNotificationFailed}
        />
      )}
    </>
  )
}
